import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AnalyticsEventName,
  LTIContext,
  ManageName,
  ManageProfilePicture,
  useAnalytics,
  useAppContext,
  useLogout,
  useModal,
  useMutationDeleteAppUserAction,
  useMutationResetAppUserAction,
} from "@examspark/common-app";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  Text,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";

import Breadcrumbs from "../../components/common/Breadcrumbs";
import PageHead from "../../components/common/PageHead";
import PageLayout from "../../components/common/PageLayout";
import { CampfireAssessmentBanner } from "../../components/dashboard/campfire-assessment-banner/CampfireAssessmentBanner";
import { useFlags } from "../../hooks/useFlags";
import RedirectToCampfireModal from "../../redirect/RedirectToCampfireModal";

const AccountRoute = () => {
  const [context, dispatch] = useAppContext();
  const flags = useFlags();
  const redirectToCampfireAssessmentModal = useModal();
  const logout = useLogout();
  const lti = useContext(LTIContext);
  const navigate = useNavigate();
  const resetAppUserMutation = useMutationResetAppUserAction();
  const deleteAppUserMutation = useMutationDeleteAppUserAction();
  const analytics = useAnalytics();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  return (
    <>
      <RedirectToCampfireModal
        {...redirectToCampfireAssessmentModal.modalProps}
      />
      <PageLayout>
        <VSpace style={{ gap: "3rem" }}>
          <PageHead>
            <Breadcrumbs items={Breadcrumbs.Dashboard()} />
            <Text.H1>My Account</Text.H1>
          </PageHead>
          <VSpace>
            <Text.H2>Logout</Text.H2>
            <Text.Body>End your session on this device.</Text.Body>
            <Toolbar>
              <Button
                title="Logout"
                isLoading={isLoggingOut}
                disabled={!!lti}
                onClick={() => {
                  setIsLoggingOut(true);
                  logout("/");
                }}
              >
                Logout
              </Button>
            </Toolbar>
          </VSpace>
          <VSpace>
            <Text.H2>Manage Name</Text.H2>
            <Text.Body>
              Update the name that appears in this application. Your email
              address is displayed for informational purposes only and cannot be
              updated.
            </Text.Body>
            <div>
              <ManageName
                user={context.user}
                onChange={(name) => {
                  if (!context.user?.id) return;

                  dispatch({
                    type: "SET_USER",
                    data: {
                      ...context.user,
                      ...name,
                    },
                  });
                }}
              />
            </div>
          </VSpace>
          <VSpace>
            <Text.H2>Profile Picture</Text.H2>
            <Text.Body>
              Make your experience more personal by adding your profile picture
              to comments, posts, and other activities you carry out in this
              app.
            </Text.Body>
            <div>
              <ManageProfilePicture
                user={context.user}
                onChange={(hash) => {
                  if (!context.user?.id) return;

                  dispatch({
                    type: "SET_USER",
                    data: {
                      ...context.user,
                      avatarHash: hash,
                    },
                  });
                }}
              />
            </div>
          </VSpace>
          <VSpace>
            <Text.H2 style={{ color: "red" }}>Danger Zone</Text.H2>
            <Toolbar>
              <Button
                title="Reset Account"
                onClick={async () => {
                  await ConfirmModal.show({
                    onResolving: async (result) => {
                      if (result) {
                        if (!context.user?.id) {
                          console.error("No user ID");
                          return;
                        }
                        if (!context.appLicense?.appKey) {
                          console.error("No appKey ID");
                          return;
                        }
                        await resetAppUserMutation.mutateAsync({
                          appKey: context.appLicense?.appKey,
                          userId: context.user.id,
                        });
                        analytics.trackEvent(AnalyticsEventName.AccountReset);
                        navigate("/");
                      }
                    },
                    children: (
                      <CautionBanner variant="plain">
                        Are you sure you want to reset your account? You will
                        lose all your data.
                      </CautionBanner>
                    ),
                  });
                }}
              >
                Reset Account
              </Button>
              <Button
                title="Delete Account"
                onClick={async () => {
                  await ConfirmModal.show({
                    onResolving: async (result) => {
                      if (result) {
                        if (!context.user?.id) {
                          console.error("No user ID");
                          return;
                        }
                        if (!context.appLicense?.appKey) {
                          console.error("No appKey ID");
                          return;
                        }
                        await deleteAppUserMutation.mutateAsync({
                          appKey: context.appLicense?.appKey,
                          userId: context.user.id,
                        });
                        analytics.trackEvent(AnalyticsEventName.AccountDelete);
                        logout("/");
                      }
                    },
                    children: (
                      <CautionBanner variant="plain">
                        Are you sure you want to delete your account? You will
                        lose all your data!
                      </CautionBanner>
                    ),
                  });
                }}
              >
                Delete Account
              </Button>
            </Toolbar>
          </VSpace>
        </VSpace>
        <aside>
          {flags.tcmpcAssessmentAppShown && (
            <CampfireAssessmentBanner
              disabled={!flags.tcmpcAssessmentAppEnabled}
            />
          )}
        </aside>
      </PageLayout>
    </>
  );
};

export default AccountRoute;
